import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { PageHeader } from "@/components/shared";
import { PIPELINE_STAGES } from "@/config/trackers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, GripVertical, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STAGE_COLOR = {
  "Idea/Brief": "#64748B", "Shoot Scheduled": "#38BDF8", "Shot": "#A78BFA",
  "In Edit": "#F59E0B", "Copy Written": "#0EA5E9", "Design Ready": "#EC4899",
  "Approved": "#059669", "Published": "#C9A24B",
};
const PRIORITY_COLOR = { High: "rag-red", Medium: "rag-amber", Low: "rag-green" };

export default function ContentPipeline() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "", assignees: "", priority: "Medium", stage: "Idea/Brief", notes: "" });
  const [dragId, setDragId] = useState(null);

  const { data: items = [] } = useQuery({ queryKey: ["pipeline"], queryFn: async () => (await api.get("/pipeline")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["pipeline"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const saveMut = useMutation({ mutationFn: async (p) => api.post("/pipeline", p), onSuccess: () => { invalidate(); setOpen(false); toast.success("Card added"); } });
  const moveMut = useMutation({ mutationFn: async ({ id, stage }) => api.put(`/pipeline/${id}`, { stage }), onSuccess: invalidate });
  const delMut = useMutation({ mutationFn: async (id) => api.delete(`/pipeline/${id}`), onSuccess: () => { invalidate(); toast.success("Removed"); } });

  const onDrop = (stage) => { if (dragId) { moveMut.mutate({ id: dragId, stage }); setDragId(null); } };

  return (
    <div>
      <PageHeader title="Content Pipeline" subtitle="Every piece across all 7 roles in one board — drag cards to move stages">
        <Button data-testid="add-pipeline-btn" onClick={() => { setForm({ title: "", type: "", assignees: "", priority: "Medium", stage: "Idea/Brief", notes: "" }); setOpen(true); }} className="bg-gold text-navy hover:bg-gold-hover gap-2">
          <Plus className="w-4 h-4" /> Add Card
        </Button>
      </PageHeader>

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {PIPELINE_STAGES.map((stage) => {
          const cards = items.filter((i) => i.stage === stage);
          return (
            <div
              key={stage}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(stage)}
              className="w-[260px] shrink-0 rounded-lg bg-surface border border-surface-hover"
              data-testid={`stage-${stage.replace(/[^a-zA-Z]/g, "")}`}
            >
              <div className="p-3 border-b border-surface-hover flex items-center justify-between" style={{ borderTopColor: STAGE_COLOR[stage], borderTopWidth: 3 }}>
                <span className="text-sm font-semibold">{stage}</span>
                <span className="text-xs text-slatetext bg-navy px-2 py-0.5 rounded-full">{cards.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px]">
                {cards.map((c) => (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={() => setDragId(c.id)}
                    className="rounded-md bg-navy border border-surface-hover p-3 cursor-grab active:cursor-grabbing hover:border-gold transition-colors duration-200"
                    style={{ borderLeftColor: STAGE_COLOR[stage], borderLeftWidth: 3 }}
                    data-testid={`card-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium leading-snug">{c.title}</span>
                      <button onClick={() => delMut.mutate(c.id)} className="text-slatetext hover:text-[#DC2626] transition-colors duration-200 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {c.type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-hover text-slatetext">{c.type}</span>}
                      {c.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIORITY_COLOR[c.priority]}`}>{c.priority}</span>}
                    </div>
                    {c.assignees && <div className="text-xs text-slatetext mt-2 flex items-center gap-1"><GripVertical className="w-3 h-3" />{c.assignees}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-surface-hover text-white" data-testid="pipeline-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Pipeline Card</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-slatetext text-xs mb-1.5 block">Title</Label><Input data-testid="pipeline-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-navy border-surface-hover text-white focus:border-gold" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-slatetext text-xs mb-1.5 block">Type</Label><Input data-testid="pipeline-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Reel / Static Ad / Blog" className="bg-navy border-surface-hover text-white focus:border-gold" /></div>
              <div><Label className="text-slatetext text-xs mb-1.5 block">Priority</Label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full bg-navy border border-surface-hover rounded-md px-3 py-2 text-sm focus:border-gold outline-none">
                  {["High", "Medium", "Low"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-slatetext text-xs mb-1.5 block">Assignee(s)</Label><Input data-testid="pipeline-assignees" value={form.assignees} onChange={(e) => setForm({ ...form, assignees: e.target.value })} className="bg-navy border-surface-hover text-white focus:border-gold" /></div>
              <div><Label className="text-slatetext text-xs mb-1.5 block">Stage</Label>
                <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="w-full bg-navy border border-surface-hover rounded-md px-3 py-2 text-sm focus:border-gold outline-none">
                  {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slatetext">Cancel</Button>
            <Button data-testid="save-pipeline" onClick={() => { if (!form.title) { toast.error("Title required"); return; } saveMut.mutate(form); }} className="bg-gold text-navy hover:bg-gold-hover">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
